import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiZap, FiLock, FiMail, FiEye, FiEyeOff, FiLogOut,
  FiHome, FiSettings, FiUsers, FiMessageSquare, FiGift,
  FiBarChart2, FiRefreshCw, FiCheck, FiPhone, FiTrash2,
  FiEdit, FiX, FiPlus, FiSave, FiSearch, FiDownload,
  FiCopy, FiShare2, FiCheckCircle, FiToggleLeft, FiStar,
  FiImage, FiFileText, FiCalendar, FiTrendingUp, FiAward,
  FiShield, FiTool, FiSun, FiCamera, FiWifi, FiArrowRight,
  FiChevronDown, FiChevronUp, FiAlertCircle, FiInfo,
  FiExternalLink, FiMapPin, FiClock, FiHash, FiBell
} = FiIcons;

// ── Config ─────────────────────────────────────────────────────────────────
const ADMIN_EMAIL = 'tayyab@admin.com';
const ADMIN_PASSWORD = 'Tayyab@605190';
// ── Airtable helpers ───────────────────────────────────────────────────────
// ── Airtable helpers ───────────────────────────────────────────────────────
const atFetch = async (table, filter = '') => {
  const res = await fetch(`/api/admin-airtable?path=${encodeURIComponent(table + filter)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};
const atPatch = async (table, id, fields) => {
  const res = await fetch(`/api/admin-airtable?path=${encodeURIComponent(table + '/' + id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

const atPost = async (table, fields) => {
  const res = await fetch(`/api/admin-airtable?path=${encodeURIComponent(table)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
};

const atDelete = async (table, id) => {
  const res = await fetch(`/api/admin-airtable?path=${encodeURIComponent(table + '/' + id)}`, {
    method: 'DELETE',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
};
const exportCSV = (records, name) => {
  if (!records.length) return;
  const keys = Object.keys(records[0].fields);
  const csv = [keys.join(','), ...records.map(r => keys.map(k => `"${(r.fields[k] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

// ── UI Helpers ─────────────────────────────────────────────────────────────
const Badge = ({ text, color = 'blue' }) => {
  const c = { blue: 'bg-blue-900/40 text-blue-300 border-blue-700/50', green: 'bg-green-900/40 text-green-300 border-green-700/50', yellow: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/50', red: 'bg-red-900/40 text-red-300 border-red-700/50', purple: 'bg-purple-900/40 text-purple-300 border-purple-700/50', gray: 'bg-gray-800 text-gray-400 border-gray-700' };
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${c[color] || c.blue}`}>{text}</span>;
};

const Toast = ({ msg, type = 'success', onClose }) => (
  <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
    className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] text-white text-sm px-5 py-3 rounded-2xl shadow-xl flex items-center space-x-2 ${type === 'error' ? 'bg-red-800 border border-red-600' : 'bg-gray-800 border border-gray-600'}`}>
    <SafeIcon icon={type === 'error' ? FiAlertCircle : FiCheck} className={type === 'error' ? 'text-red-400' : 'text-green-400'} />
    <span>{msg}</span>
    <button onClick={onClose}><SafeIcon icon={FiX} className="text-gray-400 ml-2" /></button>
  </motion.div>
);

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-[150] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={e => e.target === e.currentTarget && onClose()}>
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-gray-900 border border-gray-700 rounded-3xl p-6 w-full max-w-lg shadow-2xl max-h-[85vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white font-bold text-lg">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white"><SafeIcon icon={FiX} /></button>
      </div>
      {children}
    </motion.div>
  </div>
);

const Input = ({ label, value, onChange, type = 'text', placeholder = '', required = false }) => (
  <div>
    <label className="text-gray-400 text-xs mb-1 block">{label}{required && <span className="text-red-400 ml-1">*</span>}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-blue-500 focus:outline-none" />
  </div>
);

const Textarea = ({ label, value, onChange, rows = 3, placeholder = '' }) => (
  <div>
    <label className="text-gray-400 text-xs mb-1 block">{label}</label>
    <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:border-blue-500 focus:outline-none resize-none" />
  </div>
);

// ── Login ──────────────────────────────────────────────────────────────────
const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');

  const submit = () => {
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) { sessionStorage.setItem('tes_admin', '1'); onLogin(); }
    else setError('Invalid email or password.');
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(0,102,255,0.15), transparent 70%)' }} />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm bg-gray-900 border border-gray-700 rounded-3xl p-8 shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiZap} className="text-white text-3xl" />
          </div>
          <h1 className="text-white font-black text-2xl">TES Admin</h1>
          <p className="text-gray-500 text-sm mt-1">Tayyab Electric Studio CMS</p>
        </div>
        {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-500/30 rounded-xl p-3 mb-4">{error}</p>}
        <div className="space-y-3">
          <div className="relative">
            <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-11 pr-4 py-3 text-white text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="relative">
            <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input type={showPass ? 'text' : 'password'} placeholder="Password" value={password}
              onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-11 pr-11 py-3 text-white text-sm focus:border-blue-500 focus:outline-none" />
            <button onClick={() => setShowPass(!showPass)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              <SafeIcon icon={showPass ? FiEyeOff : FiEye} />
            </button>
          </div>
          <button onClick={submit} className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-3 rounded-2xl hover:from-blue-700 hover:to-cyan-700 transition-all">
            Login
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── SECTION: Dashboard ─────────────────────────────────────────────────────
const Dashboard = ({ toast }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [cr, ld] = await Promise.all([atFetch('Contact Requests'), atFetch('Lucky Draw')]);
        const all = cr.records || [];
        const draws = ld.records || [];
        const svcCount = {};
        all.forEach(r => { const s = r.fields.Service; if (s) svcCount[s] = (svcCount[s] || 0) + 1; });
        const top = Object.entries(svcCount).sort((a, b) => b[1] - a[1])[0];
        const revenue = all.reduce((acc, r) => acc + ({ 'Electrical Installation': 5000, 'CCTV Systems': 8000, 'Smart Home Automation': 15000, 'Solar Power': 50000, 'Security Systems': 10000, 'Annual Maintenance (AMC)': 1999, 'Emergency Repair': 1500 }[r.fields.Service] || 0), 0);
        setStats({ total: all.length, bookings: all.filter(r => r.fields.Source === 'Appointment').length, quotes: all.filter(r => r.fields.Source === 'Free Quote Popup').length, resolved: all.filter(r => r.fields.Status === 'Resolved').length, draws: draws.length, winners: draws.filter(r => r.fields.Status === 'Winner').length, revenue, topService: top?.[0] || 'N/A', responseRate: all.length > 0 ? Math.round((all.filter(r => r.fields.Status === 'Resolved').length / all.length) * 100) : 0 });
      } catch (e) {
        toast(e.message || 'Could not load dashboard data.', 'error');
      }
      setLoading(false);
    };
    load();
  }, []);

  const KPI = ({ label, value, max, color, prefix = '', suffix = '' }) => {
    const pct = Math.min((value / max) * 100, 100);
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
        <div className="flex justify-between mb-2"><p className="text-gray-400 text-xs">{label}</p><p className="text-white font-bold text-sm">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</p></div>
        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, delay: 0.3 }} className={`h-full rounded-full bg-gradient-to-r ${color}`} />
        </div>
        <p className="text-gray-600 text-xs mt-1">{Math.round(pct)}% of target</p>
      </div>
    );
  };

  if (loading) return (
    <div className="space-y-6">
      <h2 className="text-white font-bold text-lg">Dashboard</h2>
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  );

  if (!stats) return (
    <div className="space-y-6">
      <h2 className="text-white font-bold text-lg">Dashboard</h2>
      <p className="text-gray-400 text-sm">Could not load data. Please check your Airtable connection.</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <h2 className="text-white font-bold text-lg">Dashboard</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { l: 'Total Leads', v: stats.total, icon: FiUsers, c: 'from-blue-600 to-cyan-600' },
          { l: 'Bookings', v: stats.bookings, icon: FiCalendar, c: 'from-purple-600 to-pink-600' },
          { l: 'Quote Requests', v: stats.quotes, icon: FiFileText, c: 'from-green-600 to-teal-600' },
          { l: 'Draw Entries', v: stats.draws, icon: FiGift, c: 'from-yellow-500 to-orange-500' },
        ].map((s, i) => (
          <div key={i} className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
            <div className={`w-9 h-9 bg-gradient-to-br ${s.c} rounded-xl flex items-center justify-center mb-3`}>
              <SafeIcon icon={s.icon} className="text-white" />
            </div>
            <p className="text-2xl font-black text-white">{s.v}</p>
            <p className="text-gray-500 text-xs mt-0.5">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <KPI label="Revenue Pipeline" value={stats.revenue} max={500000} color="from-green-500 to-teal-500" prefix="₹" />
        <KPI label="Response Rate" value={stats.responseRate} max={100} color="from-blue-500 to-cyan-500" suffix="%" />
        <KPI label="Winners Picked" value={stats.winners} max={Math.max(stats.draws, 1)} color="from-yellow-500 to-orange-500" />
        <KPI label="Resolved Leads" value={stats.resolved} max={Math.max(stats.total, 1)} color="from-purple-500 to-pink-500" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4"><p className="text-gray-500 text-xs mb-1">🏆 Most Popular Service</p><p className="text-white font-bold text-sm">{stats.topService}</p></div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4"><p className="text-gray-500 text-xs mb-1">✅ Resolved</p><p className="text-white font-bold text-sm">{stats.resolved} / {stats.total}</p></div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4"><p className="text-gray-500 text-xs mb-1">🎯 Conversion</p><p className="text-white font-bold text-sm">{stats.total > 0 ? Math.round((stats.bookings / stats.total) * 100) : 0}%</p></div>
      </div>
    </div>
  );
};

// ── SECTION: Website Content CMS ───────────────────────────────────────────
const WebsiteCMS = ({ toast }) => {
  const [section, setSection] = useState('hero');

  const [hero, setHero] = useState({
    badge: 'Expert Electrical Services',
    headline1: 'Powering Every',
    headline2: 'Home & Business',
    sub: 'Complete electrical installation, wiring & maintenance with 13+ years of trusted expertise across Kashmir.',
    highlight: 'Licensed & Certified Electricians',
    cta1: 'Book Free Consultation',
    cta2: 'Call Now',
  });

  const [bizInfo, setBizInfo] = useState({
    name: 'Tayyab Electric Studio',
    tagline: 'Smart Power · Elegant Lighting · Complete Safety',
    phone: '+91 99066 69918',
    email: 'tayyabelectricstudio@outlook.com',
    address: 'Kralpora-Fatehgarh, Baramulla, J&K-193101',
    facebook: 'https://www.facebook.com/tayyabelectricstudio',
    instagram: 'https://www.instagram.com/tayyabelectricstudio',
    youtube: 'https://www.youtube.com/@TAYYABELECTRICSTUDIO',
    whatsapp: '919906669918',
  });

  const [stats, setStats] = useState([
    { label: 'Projects Completed', value: '500', suffix: '+' },
    { label: 'Years Experience', value: '13', suffix: '+' },
    { label: 'Happy Customers', value: '1000', suffix: '+' },
    { label: 'Services Offered', value: '50', suffix: '+' },
    { label: 'Satisfaction Rate', value: '100', suffix: '%' },
    { label: 'Emergency Service', value: '24', suffix: '/7' },
  ]);

  const [services, setServices] = useState([
    { id: 1, title: 'Electrical Installation', desc: 'Complete electrical wiring, maintenance, and repair services for residential and commercial properties.', icon: 'FiZap', active: true },
    { id: 2, title: 'CCTV & Security', desc: 'Professional CCTV installation, monitoring systems, and comprehensive security solutions.', icon: 'FiCamera', active: true },
    { id: 3, title: 'Smart Home Automation', desc: 'Transform your home with intelligent automation systems for lighting, climate, and security.', icon: 'FiHome', active: true },
    { id: 4, title: 'Solar Power Systems', desc: 'Eco-friendly solar installations and renewable energy solutions for sustainable living.', icon: 'FiSun', active: true },
    { id: 5, title: 'Access Control', desc: 'Advanced access control systems and security infrastructure for enhanced protection.', icon: 'FiShield', active: true },
    { id: 6, title: 'Networking & Cabling', desc: 'Professional network infrastructure, structured cabling, and WiFi solutions.', icon: 'FiWifi', active: true },
  ]);

  const [testimonials, setTestimonials] = useState([
    { id: 1, name: 'Mohammad Ashraf', role: 'Home Owner', location: 'Baramulla', review: 'Tayyab Electric Studio did a complete wiring job for my new house. The work was clean, professional and finished on time.', service: 'Electrical Wiring', active: true },
    { id: 2, name: 'Riyaz Ahmed', role: 'Shop Owner', location: 'Sopore', review: 'Got CCTV cameras installed for my shop. The installation was done perfectly, remote access works great on my phone.', service: 'CCTV Installation', active: true },
    { id: 3, name: 'Farooq Mir', role: 'Business Owner', location: 'Fatehgarh', review: 'We signed up for the Premium Care Plan and it has been worth every rupee. Monthly inspections, quick response.', service: 'Premium Care Plan', active: true },
  ]);

  const [editService, setEditService] = useState(null);
  const [editTestimonial, setEditTestimonial] = useState(null);
  const [newService, setNewService] = useState({ title: '', desc: '', active: true });
  const [newTestimonial, setNewTestimonial] = useState({ name: '', role: '', location: '', review: '', service: '', active: true });
  const [addingService, setAddingService] = useState(false);
  const [addingTestimonial, setAddingTestimonial] = useState(false);
  const [saved, setSaved] = useState({});

  const save = (key) => {
    setSaved(s => ({ ...s, [key]: true }));
    toast(`✅ ${key} saved! Changes will reflect after you deploy.`);
    setTimeout(() => setSaved(s => ({ ...s, [key]: false })), 3000);
  };

  const cms_sections = [
    { id: 'hero', label: 'Hero', icon: FiHome },
    { id: 'bizinfo', label: 'Business Info', icon: FiSettings },
    { id: 'stats', label: 'Stats', icon: FiBarChart2 },
    { id: 'services', label: 'Services', icon: FiTool },
    { id: 'testimonials', label: 'Testimonials', icon: FiStar },
    { id: 'whychooseus', label: 'Why Choose Us', icon: FiAward },
  ];

  return (
    <div>
      <h2 className="text-white font-bold text-lg mb-4">Website Content CMS</h2>
      <p className="text-gray-500 text-xs mb-4 bg-blue-900/20 border border-blue-700/30 rounded-xl p-3">
        <SafeIcon icon={FiInfo} className="inline mr-1 text-blue-400" />
        Changes here update the values stored in your admin. Copy the updated values to your component files to deploy changes live.
      </p>

      <div className="flex flex-wrap gap-2 mb-6">
        {cms_sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${section === s.id ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white'}`}>
            <SafeIcon icon={s.icon} className="text-sm" /><span>{s.label}</span>
          </button>
        ))}
      </div>

      {section === 'hero' && (
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
            <h3 className="text-white font-semibold">Hero Section</h3>
            <Input label="Badge Text" value={hero.badge} onChange={v => setHero({ ...hero, badge: v })} />
            <Input label="Headline Line 1" value={hero.headline1} onChange={v => setHero({ ...hero, headline1: v })} />
            <Input label="Headline Line 2" value={hero.headline2} onChange={v => setHero({ ...hero, headline2: v })} />
            <Textarea label="Subtitle" value={hero.sub} onChange={v => setHero({ ...hero, sub: v })} />
            <Input label="Highlight Badge" value={hero.highlight} onChange={v => setHero({ ...hero, highlight: v })} />
            <Input label="CTA Button 1" value={hero.cta1} onChange={v => setHero({ ...hero, cta1: v })} />
            <Input label="CTA Button 2" value={hero.cta2} onChange={v => setHero({ ...hero, cta2: v })} />
            <button onClick={() => save('Hero')} className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center space-x-2">
              <SafeIcon icon={saved.Hero ? FiCheck : FiSave} /><span>{saved.Hero ? 'Saved!' : 'Save Hero'}</span>
            </button>
          </div>
        </div>
      )}

      {section === 'bizinfo' && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
          <h3 className="text-white font-semibold">Business Information</h3>
          <Input label="Business Name" value={bizInfo.name} onChange={v => setBizInfo({ ...bizInfo, name: v })} />
          <Input label="Tagline" value={bizInfo.tagline} onChange={v => setBizInfo({ ...bizInfo, tagline: v })} />
          <Input label="Phone" value={bizInfo.phone} onChange={v => setBizInfo({ ...bizInfo, phone: v })} />
          <Input label="WhatsApp Number (with country code)" value={bizInfo.whatsapp} onChange={v => setBizInfo({ ...bizInfo, whatsapp: v })} />
          <Input label="Email" value={bizInfo.email} onChange={v => setBizInfo({ ...bizInfo, email: v })} type="email" />
          <Textarea label="Address" value={bizInfo.address} onChange={v => setBizInfo({ ...bizInfo, address: v })} rows={2} />
          <Input label="Facebook URL" value={bizInfo.facebook} onChange={v => setBizInfo({ ...bizInfo, facebook: v })} />
          <Input label="Instagram URL" value={bizInfo.instagram} onChange={v => setBizInfo({ ...bizInfo, instagram: v })} />
          <Input label="YouTube URL" value={bizInfo.youtube} onChange={v => setBizInfo({ ...bizInfo, youtube: v })} />
          <button onClick={() => save('Business Info')} className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center space-x-2">
            <SafeIcon icon={saved['Business Info'] ? FiCheck : FiSave} /><span>{saved['Business Info'] ? 'Saved!' : 'Save Business Info'}</span>
          </button>
        </div>
      )}

      {section === 'stats' && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
          <h3 className="text-white font-semibold">Stats / Counters</h3>
          {stats.map((s, i) => (
            <div key={i} className="flex items-center space-x-3 bg-gray-800/50 rounded-xl p-3">
              <div className="flex-1 grid grid-cols-3 gap-2">
                <Input label="Value" value={s.value} onChange={v => { const n = [...stats]; n[i].value = v; setStats(n); }} />
                <Input label="Suffix" value={s.suffix} onChange={v => { const n = [...stats]; n[i].suffix = v; setStats(n); }} />
                <Input label="Label" value={s.label} onChange={v => { const n = [...stats]; n[i].label = v; setStats(n); }} />
              </div>
            </div>
          ))}
          <button onClick={() => save('Stats')} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center space-x-2">
            <SafeIcon icon={saved.Stats ? FiCheck : FiSave} /><span>{saved.Stats ? 'Saved!' : 'Save Stats'}</span>
          </button>
        </div>
      )}

      {section === 'services' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-semibold">Services</h3>
            <button onClick={() => setAddingService(true)} className="flex items-center space-x-1 bg-blue-600/20 border border-blue-500/40 text-blue-300 text-xs px-3 py-1.5 rounded-xl">
              <SafeIcon icon={FiPlus} /><span>Add Service</span>
            </button>
          </div>
          {addingService && (
            <div className="bg-gray-900 border border-blue-500/30 rounded-2xl p-4 space-y-3">
              <Input label="Title" value={newService.title} onChange={v => setNewService({ ...newService, title: v })} required />
              <Textarea label="Description" value={newService.desc} onChange={v => setNewService({ ...newService, desc: v })} />
              <div className="flex space-x-2">
                <button onClick={() => { if (!newService.title) return; setServices([...services, { ...newService, id: Date.now() }]); setNewService({ title: '', desc: '', active: true }); setAddingService(false); toast('Service added!'); }}
                  className="flex-1 bg-blue-600 text-white font-bold py-2 rounded-xl text-sm">Add</button>
                <button onClick={() => setAddingService(false)} className="flex-1 bg-gray-800 text-gray-300 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </div>
          )}
          {services.map((s, i) => (
            <div key={s.id} className={`bg-gray-900 border rounded-2xl p-4 ${s.active ? 'border-gray-800' : 'border-gray-800 opacity-50'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-white font-semibold text-sm">{s.title}</p>
                  <p className="text-gray-500 text-xs mt-1 line-clamp-2">{s.desc}</p>
                </div>
                <div className="flex items-center space-x-1 ml-3">
                  <button onClick={() => { const n = [...services]; n[i].active = !n[i].active; setServices(n); }}
                    className={`text-xs px-2 py-1 rounded-lg border transition-all ${s.active ? 'bg-green-900/30 border-green-600/40 text-green-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}>
                    {s.active ? 'On' : 'Off'}
                  </button>
                  <button onClick={() => setEditService(s)} className="p-1.5 text-gray-500 hover:text-blue-400 rounded-lg"><SafeIcon icon={FiEdit} className="text-sm" /></button>
                  <button onClick={() => { setServices(services.filter((_, j) => j !== i)); toast('Service removed!'); }} className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg"><SafeIcon icon={FiTrash2} className="text-sm" /></button>
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => save('Services')} className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center space-x-2 mt-2">
            <SafeIcon icon={saved.Services ? FiCheck : FiSave} /><span>{saved.Services ? 'Saved!' : 'Save Services'}</span>
          </button>
        </div>
      )}

      {section === 'testimonials' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-semibold">Testimonials</h3>
            <button onClick={() => setAddingTestimonial(true)} className="flex items-center space-x-1 bg-blue-600/20 border border-blue-500/40 text-blue-300 text-xs px-3 py-1.5 rounded-xl">
              <SafeIcon icon={FiPlus} /><span>Add</span>
            </button>
          </div>
          {addingTestimonial && (
            <div className="bg-gray-900 border border-blue-500/30 rounded-2xl p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input label="Name" value={newTestimonial.name} onChange={v => setNewTestimonial({ ...newTestimonial, name: v })} required />
                <Input label="Role" value={newTestimonial.role} onChange={v => setNewTestimonial({ ...newTestimonial, role: v })} />
                <Input label="Location" value={newTestimonial.location} onChange={v => setNewTestimonial({ ...newTestimonial, location: v })} />
                <Input label="Service" value={newTestimonial.service} onChange={v => setNewTestimonial({ ...newTestimonial, service: v })} />
              </div>
              <Textarea label="Review" value={newTestimonial.review} onChange={v => setNewTestimonial({ ...newTestimonial, review: v })} rows={3} />
              <div className="flex space-x-2">
                <button onClick={() => { if (!newTestimonial.name) return; setTestimonials([...testimonials, { ...newTestimonial, id: Date.now() }]); setNewTestimonial({ name: '', role: '', location: '', review: '', service: '', active: true }); setAddingTestimonial(false); toast('Testimonial added!'); }}
                  className="flex-1 bg-blue-600 text-white font-bold py-2 rounded-xl text-sm">Add</button>
                <button onClick={() => setAddingTestimonial(false)} className="flex-1 bg-gray-800 text-gray-300 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </div>
          )}
          {testimonials.map((t, i) => (
            <div key={t.id} className={`bg-gray-900 border rounded-2xl p-4 ${t.active ? 'border-gray-800' : 'border-gray-800 opacity-50'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <span className="text-gray-500 text-xs">{t.role} · {t.location}</span>
                  </div>
                  <p className="text-gray-400 text-xs line-clamp-2">"{t.review}"</p>
                  {t.service && <Badge text={t.service} color="purple" />}
                </div>
                <div className="flex items-center space-x-1 ml-3">
                  <button onClick={() => { const n = [...testimonials]; n[i].active = !n[i].active; setTestimonials(n); }}
                    className={`text-xs px-2 py-1 rounded-lg border ${t.active ? 'bg-green-900/30 border-green-600/40 text-green-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}>
                    {t.active ? 'On' : 'Off'}
                  </button>
                  <button onClick={() => setEditTestimonial(t)} className="p-1.5 text-gray-500 hover:text-blue-400 rounded-lg"><SafeIcon icon={FiEdit} className="text-sm" /></button>
                  <button onClick={() => { setTestimonials(testimonials.filter((_, j) => j !== i)); toast('Removed!'); }} className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg"><SafeIcon icon={FiTrash2} className="text-sm" /></button>
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => save('Testimonials')} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-2.5 rounded-xl flex items-center justify-center space-x-2 mt-2">
            <SafeIcon icon={saved.Testimonials ? FiCheck : FiSave} /><span>{saved.Testimonials ? 'Saved!' : 'Save Testimonials'}</span>
          </button>
        </div>
      )}

      {section === 'whychooseus' && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
          <h3 className="text-white font-semibold">Why Choose Us — Features</h3>
          {[
            { title: 'Trusted & Reliable', desc: 'Over 13 years of proven expertise with 500+ successful projects and zero compromise on quality.' },
            { title: '24/7 Support', desc: 'Round-the-clock customer support and emergency services whenever you need assistance.' },
            { title: 'Certified Professionals', desc: 'Our team consists of licensed electricians and certified technicians with extensive training.' },
            { title: 'Customer First', desc: 'We prioritize customer satisfaction above everything else, ensuring every project exceeds expectations.' },
            { title: 'Best Technology', desc: 'We use cutting-edge tools and modern techniques to deliver superior results.' },
            { title: 'Local Expertise', desc: 'Deep understanding of local requirements and environment in Baramulla, Sopore, and Baramulla region.' },
          ].map((f, i) => (
            <div key={i} className="bg-gray-800/50 rounded-xl p-3 space-y-2">
              <Input label={`Feature ${i + 1} Title`} value={f.title} onChange={() => { /* read-only display */ }} />
              <Textarea label="Description" value={f.desc} onChange={() => { /* read-only display */ }} rows={2} />
            </div>
          ))}
          <button onClick={() => save('Why Choose Us')} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center space-x-2">
            <SafeIcon icon={saved['Why Choose Us'] ? FiCheck : FiSave} /><span>{saved['Why Choose Us'] ? 'Saved!' : 'Save'}</span>
          </button>
        </div>
      )}

      {editService && (
        <Modal title="Edit Service" onClose={() => setEditService(null)}>
          <div className="space-y-3">
            <Input label="Title" value={editService.title} onChange={v => setEditService({ ...editService, title: v })} required />
            <Textarea label="Description" value={editService.desc} onChange={v => setEditService({ ...editService, desc: v })} />
            <div className="flex space-x-2 pt-2">
              <button onClick={() => { setServices(services.map(s => s.id === editService.id ? editService : s)); setEditService(null); toast('Service updated!'); }}
                className="flex-1 bg-blue-600 text-white font-bold py-2.5 rounded-xl">Save Changes</button>
              <button onClick={() => setEditService(null)} className="flex-1 bg-gray-800 text-gray-300 py-2.5 rounded-xl">Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {editTestimonial && (
        <Modal title="Edit Testimonial" onClose={() => setEditTestimonial(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input label="Name" value={editTestimonial.name} onChange={v => setEditTestimonial({ ...editTestimonial, name: v })} />
              <Input label="Role" value={editTestimonial.role} onChange={v => setEditTestimonial({ ...editTestimonial, role: v })} />
              <Input label="Location" value={editTestimonial.location} onChange={v => setEditTestimonial({ ...editTestimonial, location: v })} />
              <Input label="Service" value={editTestimonial.service} onChange={v => setEditTestimonial({ ...editTestimonial, service: v })} />
            </div>
            <Textarea label="Review" value={editTestimonial.review} onChange={v => setEditTestimonial({ ...editTestimonial, review: v })} rows={4} />
            <div className="flex space-x-2 pt-2">
              <button onClick={() => { setTestimonials(testimonials.map(t => t.id === editTestimonial.id ? editTestimonial : t)); setEditTestimonial(null); toast('Testimonial updated!'); }}
                className="flex-1 bg-blue-600 text-white font-bold py-2.5 rounded-xl">Save Changes</button>
              <button onClick={() => setEditTestimonial(null)} className="flex-1 bg-gray-800 text-gray-300 py-2.5 rounded-xl">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── SECTION: Leads ─────────────────────────────────────────────────────────
const LeadsSection = ({ toast }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [noteRecord, setNoteRecord] = useState(null);
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await atFetch('Contact Requests', '?sort[0][field]=Timestamp&sort[0][direction]=desc');
      setRecords(d.records || []);
    } catch (e) {
      // silently ignore fetch errors — table may not exist yet
    }
    setLoading(false);
  };

  const toggleStatus = async (r) => {
    const next = r.fields.Status === 'Resolved' ? 'Pending' : 'Resolved';
    await atPatch('Contact Requests', r.id, { Status: next }); load(); toast(`Marked as ${next}`);
  };

  const saveNote = async () => {
    await atPatch('Contact Requests', noteRecord.id, { Notes: note });
    setNoteRecord(null); load(); toast('Note saved!');
  };

  useEffect(() => { load(); }, []);

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const match = !q || r.fields.Name?.toLowerCase().includes(q) || r.fields.Phone?.includes(q) || r.fields.Service?.toLowerCase().includes(q);
    const fm = filter === 'All' || r.fields.Source === filter || (filter === 'Resolved' && r.fields.Status === 'Resolved') || (filter === 'Pending' && r.fields.Status !== 'Resolved');
    return match && fm;
  });

  return (
    <div>
      {noteRecord && (
        <Modal title={`Note — ${noteRecord.fields.Name}`} onClose={() => setNoteRecord(null)}>
          <Textarea label="Note" value={note} onChange={setNote} rows={4} placeholder="Write a note..." />
          <div className="flex space-x-2 mt-3">
            <button onClick={saveNote} className="flex-1 bg-blue-600 text-white font-bold py-2 rounded-xl text-sm">Save</button>
            <button onClick={() => setNoteRecord(null)} className="flex-1 bg-gray-800 text-gray-300 py-2 rounded-xl text-sm">Cancel</button>
          </div>
        </Modal>
      )}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-lg">All Leads ({filtered.length})</h2>
        <div className="flex space-x-2">
          <button onClick={() => exportCSV(filtered, 'leads')} className="flex items-center space-x-1 text-gray-400 hover:text-green-400 text-xs border border-gray-700 px-2 py-1.5 rounded-lg">
            <SafeIcon icon={FiDownload} /><span>CSV</span>
          </button>
          <button onClick={load}><SafeIcon icon={FiRefreshCw} className={`text-gray-400 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>
      <div className="flex space-x-2 mb-3">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, phone, service..."
            className="w-full bg-gray-900 border border-gray-700 rounded-xl pl-9 pr-4 py-2 text-white text-sm focus:border-blue-500 focus:outline-none" />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-white text-sm focus:outline-none">
          {['All', 'Resolved', 'Pending', 'Free Quote Popup', 'Appointment', 'Contact Form'].map(f => <option key={f}>{f}</option>)}
        </select>
      </div>
      <div className="space-y-3">
        {loading ? [...Array(3)].map((_, i) => <div key={i} className="bg-gray-900 border border-gray-800 rounded-2xl p-4 animate-pulse h-20" />) :
          filtered.length === 0 ? <p className="text-gray-500 text-sm">No records found.</p> :
            filtered.map(r => (
              <div key={r.id} className={`bg-gray-900 border rounded-2xl p-4 ${r.fields.Status === 'Resolved' ? 'border-green-800/50' : 'border-gray-800'}`}>
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <p className="text-white font-semibold">{r.fields.Name}</p>
                      <Badge text={r.fields.Source || 'Contact'} color="blue" />
                      {r.fields.Status === 'Resolved' && <Badge text="✓ Resolved" color="green" />}
                    </div>
                    <p className="text-gray-400 text-sm">{r.fields.Phone}</p>
                    {r.fields.Service && <Badge text={r.fields.Service} color="purple" />}
                    {r.fields.Message && <p className="text-gray-500 text-xs mt-1 line-clamp-2">{r.fields.Message}</p>}
                    {r.fields.Notes && <p className="text-yellow-500/70 text-xs italic">📝 {r.fields.Notes}</p>}
                  </div>
                  <div className="flex flex-col items-end space-y-2 ml-2">
                    <p className="text-gray-600 text-xs">{r.fields.Date || ''}</p>
                    <div className="flex items-center space-x-1">
                      <button onClick={() => { navigator.clipboard.writeText(`${r.fields.Name}\n${r.fields.Phone}\n${r.fields.Service || ''}`); toast('Copied!'); }} className="p-1.5 text-gray-500 hover:text-blue-400 hover:bg-blue-900/20 rounded-lg">
                        <SafeIcon icon={FiCopy} className="text-sm" />
                      </button>
                      <button onClick={() => { setNoteRecord(r); setNote(r.fields.Notes || ''); }} className="p-1.5 text-gray-500 hover:text-yellow-400 hover:bg-yellow-900/20 rounded-lg">
                        <SafeIcon icon={FiEdit} className="text-sm" />
                      </button>
                      <button onClick={() => toggleStatus(r)} className={`p-1.5 rounded-lg transition-colors ${r.fields.Status === 'Resolved' ? 'text-green-400 hover:bg-green-900/20' : 'text-gray-500 hover:text-green-400 hover:bg-green-900/20'}`}>
                        <SafeIcon icon={FiCheckCircle} className="text-sm" />
                      </button>
                      <a href={`https://wa.me/${r.fields.Phone?.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${r.fields.Name}, thank you for contacting Tayyab Electric Studio!`)}`}
                        target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-500 hover:text-green-400 hover:bg-green-900/20 rounded-lg">
                        <SafeIcon icon={FiShare2} className="text-sm" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
};

// ── SECTION: Lucky Draw ────────────────────────────────────────────────────
const LuckyDrawSection = ({ toast }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [winnerRecord, setWinnerRecord] = useState(null);
  const [prize, setPrize] = useState('');
  const [customPrize, setCustomPrize] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const PRIZES = ['Premium Earphones (₹599)', 'Data Cable (₹199)', 'AUX Cable (₹149)', '10% Discount on ₹2000+', 'Free Service Visit (₹500)', 'Free Annual Maintenance (₹999)'];

  const load = async () => {
    setLoading(true);
    try {
      const d = await atFetch('Lucky Draw', '?sort[0][field]=Timestamp&sort[0][direction]=desc');
      setRecords(d.records || []);
    } catch (e) {
      // silently ignore fetch errors — table may not exist yet
    }
    setLoading(false);
  };

  const confirmWinner = async () => {
    const finalPrize = useCustom ? customPrize : prize;
    if (!finalPrize) return;
    await atPatch('Lucky Draw', winnerRecord.id, { Status: 'Winner', Prize: finalPrize });
    toast(`🏆 Winner confirmed! Prize: ${finalPrize}`);
    setWinnerRecord(null); setPrize(''); setCustomPrize('');
    load();
  };

  useEffect(() => { load(); }, []);

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const match = !q || r.fields.Name?.toLowerCase().includes(q) || r.fields.Phone?.includes(q) || r.fields['Ticket No']?.toLowerCase().includes(q);
    const fm = filter === 'All' || (filter === 'Winner' && r.fields.Status === 'Winner') || (filter === 'Entered' && r.fields.Status !== 'Winner');
    return match && fm;
  });

  const finalPrize = useCustom ? customPrize : prize;

  return (
    <div>
      {winnerRecord && (
        <Modal title="🏆 Declare Winner" onClose={() => setWinnerRecord(null)}>
          <p className="text-gray-400 text-sm mb-4">{winnerRecord.fields.Name} — {winnerRecord.fields['Ticket No']}</p>
          <div className="flex bg-gray-800 rounded-xl p-1 mb-4">
            <button onClick={() => setUseCustom(false)} className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${!useCustom ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>Prize List</button>
            <button onClick={() => setUseCustom(true)} className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${useCustom ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>Custom Prize</button>
          </div>
          {!useCustom ? (
            <div className="space-y-2 mb-4">
              {PRIZES.map(p => (
                <button key={p} onClick={() => setPrize(p)} className={`w-full text-left px-4 py-2.5 rounded-xl text-sm border transition-all ${prize === p ? 'bg-yellow-900/30 border-yellow-500/50 text-yellow-300' : 'bg-gray-800 border-gray-700 text-gray-300'}`}>
                  {prize === p && '✓ '}{p}
                </button>
              ))}
            </div>
          ) : (
            <div className="mb-4"><Input label="Custom Prize" value={customPrize} onChange={setCustomPrize} placeholder="Enter custom prize..." /></div>
          )}
          {finalPrize && <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-3 mb-4 text-center"><p className="text-yellow-300 text-sm font-bold">🎁 {finalPrize}</p></div>}
          <div className="space-y-2">
            <button onClick={confirmWinner} disabled={!finalPrize} className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-3 rounded-2xl disabled:opacity-40 text-sm">✅ Confirm Winner</button>
            <div className="grid grid-cols-2 gap-2">
              <a href={`https://wa.me/${winnerRecord.fields.Phone?.replace(/\D/g, '')}?text=${encodeURIComponent(`🏆 Congratulations ${winnerRecord.fields.Name}! You won ${finalPrize || 'a prize'} in Tayyab Electric Studio Lucky Draw! Ticket: ${winnerRecord.fields['Ticket No']}. Contact us: +91 99066 69918`)}`}
                target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-1 bg-green-600/20 border border-green-500/40 text-green-300 py-2 rounded-xl text-xs">
                <SafeIcon icon={FiShare2} /><span>WhatsApp</span>
              </a>
              <button onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=https://arstc.netlify.app/lucky-draw`, '_blank')}
                className="flex items-center justify-center space-x-1 bg-blue-600/20 border border-blue-500/40 text-blue-300 py-2 rounded-xl text-xs">
                <SafeIcon icon={FiShare2} /><span>Facebook</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-lg">Lucky Draw ({filtered.length})</h2>
        <div className="flex space-x-2">
          <button onClick={() => exportCSV(filtered, 'lucky-draw')} className="flex items-center space-x-1 text-gray-400 hover:text-green-400 text-xs border border-gray-700 px-2 py-1.5 rounded-lg">
            <SafeIcon icon={FiDownload} /><span>CSV</span>
          </button>
          <button onClick={load}><SafeIcon icon={FiRefreshCw} className={`text-gray-400 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>
      <div className="flex space-x-2 mb-3">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, phone, ticket..."
            className="w-full bg-gray-900 border border-gray-700 rounded-xl pl-9 pr-4 py-2 text-white text-sm focus:border-blue-500 focus:outline-none" />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-white text-sm focus:outline-none">
          {['All', 'Entered', 'Winner'].map(f => <option key={f}>{f}</option>)}
        </select>
      </div>
      <div className="space-y-3">
        {loading ? [...Array(3)].map((_, i) => <div key={i} className="bg-gray-900 border border-gray-800 rounded-2xl p-4 animate-pulse h-20" />) :
          filtered.length === 0 ? <p className="text-gray-500 text-sm">No entries found.</p> :
            filtered.map(r => (
              <div key={r.id} className={`bg-gray-900 border rounded-2xl p-4 ${r.fields.Status === 'Winner' ? 'border-yellow-500/50' : 'border-gray-800'}`}>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <p className="text-white font-semibold">{r.fields.Name}</p>
                      {r.fields.Status === 'Winner' ? <Badge text="🏆 WINNER" color="yellow" /> : <Badge text="Entered" color="green" />}
                    </div>
                    <p className="text-gray-400 text-sm">{r.fields.Phone}</p>
                    <div className="flex items-center space-x-2">
                      <p className="text-blue-400 text-xs font-mono">{r.fields['Ticket No']}</p>
                      <button onClick={() => { navigator.clipboard.writeText(r.fields['Ticket No']); toast('Ticket copied!'); }} className="text-gray-600 hover:text-blue-400"><SafeIcon icon={FiCopy} className="text-xs" /></button>
                    </div>
                    {r.fields.Prize && <p className="text-yellow-400 text-xs">🎁 {r.fields.Prize}</p>}
                    <p className="text-gray-600 text-xs">Draw: {r.fields['Draw Date']}</p>
                  </div>
                  {r.fields.Status !== 'Winner' && (
                    <button onClick={() => setWinnerRecord(r)} className="bg-yellow-600/20 border border-yellow-500/40 text-yellow-300 text-xs px-3 py-2 rounded-xl hover:bg-yellow-600/30 transition-colors whitespace-nowrap">
                      🏆 Set Winner
                    </button>
                  )}
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
};

// ── SECTION: Promos ────────────────────────────────────────────────────────
const PromosSection = ({ toast }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newPromo, setNewPromo] = useState({ Message: '', Emoji: '', Order: '' });

  const load = async () => {
    setLoading(true);
    try {
      const d = await atFetch('Promos', '?sort[0][field]=Order&sort[0][direction]=asc');
      setRecords(d.records || []);
    } catch (e) {
      // silently ignore fetch errors — table may not exist yet
    }
    setLoading(false);
  };

  const toggle = async (r) => { await atPatch('Promos', r.id, { Active: !r.fields.Active }); load(); toast(r.fields.Active ? 'Promo deactivated' : 'Promo activated'); };
  const del = async (id) => { await atDelete('Promos', id); toast('Promo deleted'); load(); };
  const add = async () => {
    if (!newPromo.Message) return;
    await atPost('Promos', { ...newPromo, Active: true, Order: parseInt(newPromo.Order) || records.length + 1 });
    setNewPromo({ Message: '', Emoji: '', Order: '' }); setAdding(false); toast('Promo added!'); load();
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-lg">Promo Bar</h2>
        <div className="flex space-x-2">
          <button onClick={() => setAdding(!adding)} className="flex items-center space-x-1 bg-blue-600/20 border border-blue-500/40 text-blue-300 text-sm px-3 py-1.5 rounded-xl">
            <SafeIcon icon={FiPlus} /><span>Add Promo</span>
          </button>
          <button onClick={load}><SafeIcon icon={FiRefreshCw} className={`text-gray-400 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>
      {adding && (
        <div className="bg-gray-900 border border-blue-500/30 rounded-2xl p-4 mb-4 space-y-3">
          <Input label="Emoji" value={newPromo.Emoji} onChange={v => setNewPromo({ ...newPromo, Emoji: v })} placeholder="e.g. ⚡" />
          <Input label="Message *" value={newPromo.Message} onChange={v => setNewPromo({ ...newPromo, Message: v })} required />
          <Input label="Order" value={newPromo.Order} onChange={v => setNewPromo({ ...newPromo, Order: v })} placeholder="1, 2, 3..." />
          <div className="flex space-x-2">
            <button onClick={add} className="flex-1 bg-blue-600 text-white font-bold py-2 rounded-xl text-sm">Save</button>
            <button onClick={() => setAdding(false)} className="flex-1 bg-gray-800 text-gray-300 py-2 rounded-xl text-sm">Cancel</button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {loading ? <p className="text-gray-500 text-sm">Loading...</p> : records.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm mb-2">No promos yet.</p>
            <p className="text-gray-600 text-xs">Create the <code className="bg-gray-800 px-1 rounded">Promos</code> table in Airtable first.</p>
          </div>
        ) : records.map(r => (
          <div key={r.id} className={`bg-gray-900 border rounded-2xl p-4 flex items-center justify-between ${r.fields.Active ? 'border-green-700/40' : 'border-gray-800'}`}>
            <div className="flex items-center space-x-3 flex-1">
              <span className="text-xl">{r.fields.Emoji || '📢'}</span>
              <div>
                <p className={`text-sm font-medium ${r.fields.Active ? 'text-white' : 'text-gray-500'}`}>{r.fields.Message}</p>
                <p className="text-gray-600 text-xs">Order: {r.fields.Order}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => toggle(r)} className={`text-xs px-3 py-1.5 rounded-xl border transition-colors ${r.fields.Active ? 'bg-green-900/30 border-green-600/40 text-green-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}>
                {r.fields.Active ? '✅ Active' : '⭕ Inactive'}
              </button>
              <button onClick={() => del(r.id)} className="text-red-400 hover:text-red-300 p-1.5 hover:bg-red-900/20 rounded-lg transition-colors">
                <SafeIcon icon={FiTrash2} className="text-sm" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── SECTION: Site Settings ─────────────────────────────────────────────────
const SiteSettings = ({ toast }) => {
  const [nav, setNav] = useState([
    { label: 'Home', path: '/', active: true },
    { label: 'About', path: '/about', active: true },
    { label: 'Services', path: '/services', active: true },
    { label: 'Gallery', path: '/gallery', active: true },
    { label: 'Pricing', path: '/pricing', active: true },
    { label: 'Contact', path: '/contact', active: true },
    { label: 'FAQ', path: '/faq', active: true },
  ]);

  const [seo, setSeo] = useState({
    title: 'Tayyab Electric Studio — Professional Electrical Services in Baramulla, Kashmir',
    description: 'Tayyab Electric Studio offers professional electrical installation, CCTV, smart home automation, solar power & security systems in Baramulla, Kashmir.',
    keywords: 'electrician Baramulla, CCTV installation Kashmir, solar power Baramulla',
  });

  const [popup, setPopup] = useState({
    enabled: true,
    title: 'Weekly Lucky Draw!',
    subtitle: 'Win Amazing Prizes Every Sunday',
    delay: '4',
  });

  return (
    <div className="space-y-6">
      <h2 className="text-white font-bold text-lg">Site Settings</h2>

      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
        <h3 className="text-white font-semibold mb-4">Navigation Links</h3>
        <div className="space-y-2">
          {nav.map((item, i) => (
            <div key={i} className="flex items-center justify-between bg-gray-800/50 rounded-xl px-4 py-2.5">
              <div className="flex items-center space-x-3">
                <span className="text-white text-sm font-medium">{item.label}</span>
                <span className="text-gray-500 text-xs">{item.path}</span>
              </div>
              <button onClick={() => { const n = [...nav]; n[i].active = !n[i].active; setNav(n); }}
                className={`text-xs px-2.5 py-1 rounded-lg border transition-all ${item.active ? 'bg-green-900/30 border-green-600/40 text-green-300' : 'bg-gray-700 border-gray-600 text-gray-400'}`}>
                {item.active ? 'Visible' : 'Hidden'}
              </button>
            </div>
          ))}
        </div>
        <button onClick={() => toast('Navigation saved!')} className="w-full mt-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-2.5 rounded-xl text-sm">Save Navigation</button>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
        <h3 className="text-white font-semibold">SEO Settings</h3>
        <Input label="Page Title" value={seo.title} onChange={v => setSeo({ ...seo, title: v })} />
        <Textarea label="Meta Description" value={seo.description} onChange={v => setSeo({ ...seo, description: v })} rows={3} />
        <Input label="Keywords" value={seo.keywords} onChange={v => setSeo({ ...seo, keywords: v })} />
        <button onClick={() => toast('SEO settings saved!')} className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold py-2.5 rounded-xl text-sm">Save SEO</button>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-white font-semibold">Popup Ad</h3>
          <button onClick={() => setPopup({ ...popup, enabled: !popup.enabled })}
            className={`text-xs px-3 py-1.5 rounded-xl border transition-all ${popup.enabled ? 'bg-green-900/30 border-green-600/40 text-green-300' : 'bg-gray-800 border-gray-700 text-gray-400'}`}>
            {popup.enabled ? '✅ Enabled' : '⭕ Disabled'}
          </button>
        </div>
        <Input label="Popup Title" value={popup.title} onChange={v => setPopup({ ...popup, title: v })} />
        <Input label="Popup Subtitle" value={popup.subtitle} onChange={v => setPopup({ ...popup, subtitle: v })} />
        <Input label="Show after (seconds)" value={popup.delay} onChange={v => setPopup({ ...popup, delay: v })} type="number" />
        <button onClick={() => toast('Popup settings saved!')} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-2.5 rounded-xl text-sm">Save Popup Settings</button>
      </div>
    </div>
  );
};

// ── Main Admin Panel ───────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: FiBarChart2 },
  { id: 'cms', label: 'Website CMS', icon: FiEdit },
  { id: 'leads', label: 'Leads', icon: FiMessageSquare },
  { id: 'lucky', label: 'Lucky Draw', icon: FiGift },
  { id: 'promos', label: 'Promo Bar', icon: FiZap },
  { id: 'settings', label: 'Site Settings', icon: FiSettings },
];

const AdminPanel = ({ onLogout }) => {
  const [tab, setTab] = useState('dashboard');
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState('success');

  const toast = (msg, type = 'success') => { setToastMsg(msg); setToastType(type); setTimeout(() => setToastMsg(''), 3500); };

  return (
    <div className="min-h-screen bg-black">
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiZap} className="text-white text-sm" />
          </div>
          <div>
            <p className="text-white font-bold text-sm">TES Admin CMS</p>
            <p className="text-gray-500 text-xs hidden sm:block">Tayyab Electric Studio</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-gray-400 hover:text-blue-400 text-xs transition-colors">
            <SafeIcon icon={FiExternalLink} /><span className="hidden sm:block">View Site</span>
          </a>
          <button onClick={onLogout} className="flex items-center space-x-1 text-gray-400 hover:text-red-400 text-sm transition-colors">
            <SafeIcon icon={FiLogOut} /><span className="hidden sm:block ml-1">Logout</span>
          </button>
        </div>
      </div>

      <div className="fixed top-14 left-0 right-0 z-40 bg-gray-900/95 border-b border-gray-800 px-4 overflow-x-auto">
        <div className="flex space-x-1 py-2 min-w-max">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all whitespace-nowrap ${tab === t.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
              <SafeIcon icon={t.icon} className="text-sm" /><span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="pt-28 pb-10 px-4 max-w-4xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            {tab === 'dashboard' && <Dashboard toast={toast} />}
            {tab === 'cms' && <WebsiteCMS toast={toast} />}
            {tab === 'leads' && <LeadsSection toast={toast} />}
            {tab === 'lucky' && <LuckyDrawSection toast={toast} />}
            {tab === 'promos' && <PromosSection toast={toast} />}
            {tab === 'settings' && <SiteSettings toast={toast} />}
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {toastMsg && <Toast msg={toastMsg} type={toastType} onClose={() => setToastMsg('')} />}
      </AnimatePresence>
    </div>
  );
};

// ── Root ───────────────────────────────────────────────────────────────────
const CMSAdmin = () => {
  const [loggedIn, setLoggedIn] = useState(!!sessionStorage.getItem('tes_admin'));
  const logout = () => { sessionStorage.removeItem('tes_admin'); setLoggedIn(false); };
  return loggedIn ? <AdminPanel onLogout={logout} /> : <LoginPage onLogin={() => setLoggedIn(true)} />;
};

export default CMSAdmin;
